import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseAuth } from "./useSupabaseAuth";
import { useToast } from "./use-toast";
import { useTenant } from "./useTenant";
import { microsoftOAuthCoordinator } from "@/lib/microsoftOAuthCoordinator";

const MICROSOFT_SCOPES = [
  "https://graph.microsoft.com/Calendars.Read",
  "https://graph.microsoft.com/Calendars.ReadWrite",
  "https://graph.microsoft.com/User.Read",
  "offline_access",
];

interface UseMicrosoftOAuthReturn {
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
  calendarId: string | null;
  retrySyncNow: () => Promise<boolean>;
}

export const useMicrosoftOAuth = (onSyncComplete?: () => void): UseMicrosoftOAuthReturn => {
  const { user, session } = useSupabaseAuth();
  const { toast } = useToast();
  const { tenant } = useTenant();
  const lastSyncSignature = useRef<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [calendarId, setCalendarId] = useState<string | null>(null);

  const syncTokens = useCallback(async (accessToken: string, refreshToken: string, expiresIn: number): Promise<boolean> => {
    if (!user?.id) {
      console.log("❌ No user ID available for Microsoft sync");
      return false;
    }

    // Check if this is a Microsoft OAuth session
    const provider = session?.user?.app_metadata?.provider;
    if (provider !== 'azure') {
      console.log("⏭️ useMicrosoftOAuth: Not a Microsoft OAuth session, skipping");
      return false;
    }

    console.log("🔄 useMicrosoftOAuth: Requesting token sync...", {
      expiresIn,
      scopes: MICROSOFT_SCOPES,
      userId: user.id,
    });

    // Use the global coordinator to prevent duplicate syncs
    return microsoftOAuthCoordinator.coordinateSync(user.id, async () => {
      setSyncStatus('syncing');

      try {
        const { data, error } = await supabase.functions.invoke("ef_microsoft_oauth_sync", {
          body: {
            provider: "microsoft",
            access_token: accessToken,
            token_type: session?.token_type ?? "Bearer",
            refresh_token: refreshToken,
            scopes: MICROSOFT_SCOPES,
            expires_in: expiresIn,
          },
        });

        if (error) {
          console.error("❌ useMicrosoftOAuth: Sync failed (non-critical)", error);
          setSyncStatus('error');
          lastSyncSignature.current = null;
          return false;
        }

        console.log("✅ useMicrosoftOAuth: Sync successful", data);
        
        // Extract calendar ID from response if available
        if (data?.calendar_id) {
          setCalendarId(data.calendar_id);
          console.log("📅 Detected Microsoft calendar ID:", data.calendar_id);
        }

        setSyncStatus('success');
        lastSyncSignature.current = `${accessToken}:${refreshToken}`;
        
        toast({
          title: "Microsoft Calendar Connected",
          description: data?.calendar_id 
            ? `Connected to calendar` 
            : "Your Microsoft calendar is now connected",
        });

        // Trigger callback for parent components to refresh
        if (onSyncComplete) {
          onSyncComplete();
        }

        return true;
      } catch (error) {
        console.error("💥 useMicrosoftOAuth: Unexpected error during sync (non-critical)", error);
        setSyncStatus('error');
        lastSyncSignature.current = null;
        return false;
      }
    }); // End of coordinateSync
  }, [user?.id, session?.token_type, session?.user?.app_metadata?.provider, toast, onSyncComplete]);

  const retrySyncNow = useCallback(async (): Promise<boolean> => {
    const accessToken = session?.provider_token ?? null;
    const refreshToken = session?.provider_refresh_token ?? null;

    if (!user || !accessToken || !refreshToken) {
      console.log("❌ Cannot retry Microsoft sync: missing credentials");
      toast({
        title: "Sync Error",
        description: "Missing OAuth credentials. Please reconnect your Microsoft calendar.",
        variant: "destructive",
      });
      return false;
    }

    // Clear cooldown to allow immediate retry
    microsoftOAuthCoordinator.clearCooldown(user.id);

    const expiresIn = (() => {
      if (typeof session?.expires_in === "number") {
        return Math.max(session.expires_in, 60);
      }
      if (session?.expires_at) {
        const delta = session.expires_at - Math.floor(Date.now() / 1000);
        return Math.max(delta, 60);
      }
      return 3600;
    })();

    return syncTokens(accessToken, refreshToken, expiresIn);
  }, [session, user, syncTokens, toast]);

  useEffect(() => {
    const accessToken = session?.provider_token ?? null;
    const refreshToken = session?.provider_refresh_token ?? null;
    const provider = session?.user?.app_metadata?.provider;

    console.log("🔍 useMicrosoftOAuth: Session state", {
      hasSession: !!session,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      provider,
      userId: user?.id,
    });

    // Only sync if this is a Microsoft OAuth session
    if (!user || !accessToken || !refreshToken || provider !== 'azure') {
      console.log("⏭️ useMicrosoftOAuth: Missing required data or not Microsoft OAuth, skipping sync");
      return;
    }

    const tokenSignature = `${accessToken}:${refreshToken}`;
    if (lastSyncSignature.current === tokenSignature) {
      console.log("⏭️ useMicrosoftOAuth: Token signature unchanged, skipping sync");
      return;
    }

    const expiresIn = (() => {
      if (typeof session?.expires_in === "number") {
        return Math.max(session.expires_in, 60);
      }
      if (session?.expires_at) {
        const delta = session.expires_at - Math.floor(Date.now() / 1000);
        return Math.max(delta, 60);
      }
      return 3600;
    })();

    // Small delay to ensure session is fully established
    const timeoutId = setTimeout(() => {
      syncTokens(accessToken, refreshToken, expiresIn);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [session, user, syncTokens, tenant?.id]);

  return { syncStatus, calendarId, retrySyncNow };
};
